import React, { useEffect, useRef, useState } from "react";
import { FaHeadphones, FaMicrophone, FaStop, FaTimes } from "react-icons/fa";
import { useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { Track } from "livekit-client";
import { toast } from "react-toastify";
import { setVerifiedMicrophoneEnabled } from "./microphonePublication";

function stopAudioElement(audio) {
  if (!audio) return;
  audio.pause();
  audio.srcObject?.getTracks?.().forEach((track) => track.stop());
  audio.srcObject = null;
}

export default function MicrophoneSettings({ open, onClose }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [level, setLevel] = useState(0);
  const [switching, setSwitching] = useState(false);
  const [listening, setListening] = useState(false);
  const audioRef = useRef(null);

  const getMediaTrack = () =>
    localParticipant
      ?.getTrackPublication(Track.Source.Microphone)
      ?.track?.mediaStreamTrack;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const loadDevices = async () => {
      try {
        const inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
          (device) => device.kind === "audioinput"
        );
        if (cancelled) return;
        setDevices(inputs);
        const activeDeviceId = getMediaTrack()?.getSettings?.().deviceId;
        setSelectedDeviceId(activeDeviceId || inputs[0]?.deviceId || "");
      } catch (error) {
        toast.error(`Unable to list microphones: ${error?.message || "Browser access failed."}`);
      }
    };

    loadDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", loadDevices);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.("devicechange", loadDevices);
    };
  }, [open, localParticipant]);

  useEffect(() => {
    if (!open) return undefined;
    const mediaTrack = getMediaTrack();
    if (!mediaTrack || mediaTrack.readyState !== "live") {
      setLevel(0);
      return undefined;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;
    const context = new AudioContextClass();
    const stream = new MediaStream([mediaTrack]);
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    const samples = new Uint8Array(analyser.frequencyBinCount);
    let frameId;

    const updateLevel = () => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) {
        const normalized = (sample - 128) / 128;
        sum += normalized * normalized;
      }
      setLevel(Math.min(100, Math.round(Math.sqrt(sum / samples.length) * 260)));
      frameId = requestAnimationFrame(updateLevel);
    };
    updateLevel();

    return () => {
      cancelAnimationFrame(frameId);
      source.disconnect();
      context.close().catch(() => {});
    };
  }, [open, localParticipant, selectedDeviceId]);

  useEffect(() => {
    if (!open) {
      stopAudioElement(audioRef.current);
      setListening(false);
    }
    return undefined;
  }, [open]);

  useEffect(() => () => stopAudioElement(audioRef.current), []);

  if (!open) return null;

  const switchDevice = async (deviceId) => {
    setSelectedDeviceId(deviceId);
    setSwitching(true);
    setListening(false);
    try {
      const switched = await room.switchActiveDevice("audioinput", deviceId, true);
      if (!switched) throw new Error("LiveKit could not activate this microphone.");
      const { mediaTrack } = await setVerifiedMicrophoneEnabled(localParticipant, true);
      setSelectedDeviceId(mediaTrack.getSettings?.().deviceId || deviceId);
      toast.success(`Using ${mediaTrack.label || "selected microphone"}.`);
    } catch (error) {
      toast.error(`Microphone switch failed: ${error?.message || "Try another input."}`);
    } finally {
      setSwitching(false);
    }
  };

  const toggleListening = async () => {
    if (listening) {
      stopAudioElement(audioRef.current);
      setListening(false);
      return;
    }
    const mediaTrack = getMediaTrack();
    if (!mediaTrack || mediaTrack.readyState !== "live") {
      toast.error("No live microphone track is available. Select a microphone first.");
      return;
    }
    const audio = audioRef.current || new Audio();
    audioRef.current = audio;
    audio.srcObject = new MediaStream([mediaTrack.clone()]);
    audio.volume = 1;
    try {
      await audio.play();
      setListening(true);
    } catch (error) {
      toast.error(`Self-test playback was blocked: ${error?.message || "Click again to allow audio."}`);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Microphone settings">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 text-white shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold"><FaMicrophone className="text-sky-400" /> Microphone settings</h2>
            <p className="mt-1 text-xs text-slate-400">Choose an input, speak, and confirm that the meter moves.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" aria-label="Close microphone settings"><FaTimes /></button>
        </div>

        <label className="mt-5 block text-xs font-semibold text-slate-300" htmlFor="classroom-microphone">Input device</label>
        <select
          id="classroom-microphone"
          value={selectedDeviceId}
          disabled={switching || devices.length === 0}
          onChange={(event) => switchDevice(event.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-sky-500 disabled:opacity-60"
        >
          {devices.length === 0 && <option value="">No microphone found</option>}
          {devices.map((device, index) => (
            <option key={device.deviceId || index} value={device.deviceId}>
              {device.label || `Microphone ${index + 1}`}
            </option>
          ))}
        </select>

        <div className="mt-5 rounded-xl border border-slate-700 bg-slate-950/80 p-4">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-slate-300">Speak now</span>
            <span className={level > 3 ? "font-bold text-emerald-400" : "text-amber-400"}>{level > 3 ? "Input detected" : "Waiting for sound"}</span>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-800" aria-label={`Microphone input level ${level}%`}>
            <div className={`h-full rounded-full transition-[width] duration-75 ${level > 3 ? "bg-gradient-to-r from-emerald-500 to-sky-400" : "bg-amber-500"}`} style={{ width: `${Math.max(2, level)}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-slate-500">If this stays flat while speaking, choose another microphone or check Windows Input settings.</p>
        </div>

        <button type="button" onClick={toggleListening} className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${listening ? "bg-rose-600 hover:bg-rose-500" : "bg-sky-600 hover:bg-sky-500"}`}>
          {listening ? <><FaStop /> Stop hearing myself</> : <><FaHeadphones /> Hear my microphone</>}
        </button>
        <p className="mt-2 text-center text-[10px] text-amber-300">Use headphones for playback testing to prevent echo.</p>
      </div>
    </div>
  );
}
